import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { QUERY_KEYS, STALE_TIME } from '@/libs/constants'
import type { HookParams, Invite, InviteParams } from '@/libs/interfaces'
import { invitesApi } from '@/services/api'

export function useInvitesList({ params, enabled }: HookParams<InviteParams>) {
  return useQuery<Invite[]>({
    queryKey: QUERY_KEYS.invites.list(params.orgID),
    queryFn: () => invitesApi.list(params),
    enabled: (enabled ?? true) && !!params.orgID,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useCreateInvite() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: invitesApi.create,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.invites.all,
      })
      toast.success(t('settings.users.inviteSentSuccessfully'))
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

// No onError toast here: PendingInvitesList.tsx's cancel handler already
// shows one in its catch block, and toasting in both places would duplicate
// the same message for a single failure (same discipline as useAcceptInvite
// below).
export function useCancelInvite() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: invitesApi.cancel,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.invites.all,
      })
      toast.success(t('settings.users.inviteCancelled'))
    },
  })
}

// Used once on the standalone accept-invite page — no list to invalidate.
// Intentionally no onError toast: AcceptInvite.tsx renders a dedicated inline
// error state instead, so the failure isn't silent but also isn't duplicated
// across a toast and the page body.
export function useAcceptInvite() {
  return useMutation({
    mutationFn: invitesApi.accept,
  })
}
